import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../services/Server';

interface Props {
  /** API path of the related entity (e.g. ``cell-products``). */
  targetPath: string;
  /** FK column on the related entity that points back to the current parent. */
  fkField: string;
  /** ID of the current parent entity. */
  parentId: number;
}

/**
 * Warning banner shown above a relation tab when at least one peer is
 * NDA-protected and hidden from the current user. We ask the backend
 * ``GET /{targetPath}/count?filter_field=&filter_value=`` which returns
 * ``{count, hidden}``; the banner renders only when ``hidden > 0`` so the
 * three states are distinct:
 *
 *   - no peers at all                → no banner (count=0, hidden=0)
 *   - all peers visible              → no banner (count>0, hidden=0)
 *   - at least one peer is gated     → banner   (hidden>0)
 *
 * Users with ``unrestricted_nda`` always get ``hidden=0`` from the backend,
 * so we skip the request entirely for them.
 */
const NdaRelationBanner = ({ targetPath, fkField, parentId }: Props) => {
  const { can } = useAuth();
  const enabled = !can('unrestricted_nda');

  const { data } = useQuery({
    queryKey: ['nda-count', targetPath, fkField, parentId],
    queryFn: () => api.count(targetPath, { field: fkField, value: parentId }),
    enabled,
  });

  if (!data || data.hidden <= 0) return null;
  return (
    <Alert
      severity="warning"
      icon={<LockOutlinedIcon fontSize="inherit" />}
      sx={{ mb: 2, borderRadius: 1, fontWeight: 500, alignItems: 'center' }}
    >
      Some items in this list are protected by a non-disclosure agreement and are not shown.
    </Alert>
  );
};

export default NdaRelationBanner;
